import { headerMenuAtom } from "@/atoms/header-menu";
import { searchOpenAtom, searchTermAtom } from "@/atoms/search";
import { ExpandingSearch } from "@/components/search/ExpandingSearch";
import { ChromeBar, Icon, IconButton, ListRow, Scrim, Surface, Text } from "@/components/ui";
import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { CHROME_Z_INDEX } from "@/contexts/ModalContext";
import {
  ArrowsIn,
  ArrowsOut,
  Back,
  CartIcon,
  Close,
  HomeIcon,
  Market,
  List,
  OrderIcon,
  ProfileIcon,
  type IconGlyph,
} from "@/icons";
import { useFullscreen } from "@/lib";
import { BasicInfo } from "@/types";
import { cn } from "@/lib/cn";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type NavItem = {
  to: string;
  icon: IconGlyph;
  label: string;
  /** Extra classes a specific item carries on top of the shared ones. */
  className?: string;
};

const NAV_ITEMS: NavItem[] = [
  { to: "/", icon: HomeIcon, label: "Início" },
  { to: "/info", icon: Market, label: "Informações da loja" },
  { to: "/cart", icon: CartIcon, label: "Sacola", className: "w-full" },
  { to: "/profile", icon: ProfileIcon, label: "Meu perfil" },
  { to: "/profile/orders", icon: OrderIcon, label: "Meus pedidos" },
];

/**
 * Icon column matching the header logo width (48px) on the same x, so the icon
 * sits on the store image's center axis and the label aligns with the name.
 */
const MENU_ICON_BOX_CLASS = "flex w-12 shrink-0 justify-center";


interface HamburgerMenuProps {
  basicInfo: BasicInfo;
  className?: string;
  showBackButton?: boolean;
  title?: string;
  backButtonRoute?: string;
}

export function HamburgerMenu({
  basicInfo,
  className = "",
  showBackButton = false,
  title,
  backButtonRoute,
}: HamburgerMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useAtom(headerMenuAtom);
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useAtom(searchOpenAtom);
  const [searchTerm, setSearchTerm] = useAtom(searchTermAtom);
  const { isFullscreen, toggleFullscreen, isSupported } = useFullscreen();

  function toggleMenu() {
    setSearchOpen(false);
    setIsMenuOpen(!isMenuOpen);
  }

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function handleBackButton() {
    if (backButtonRoute) {
      navigate(backButtonRoute, { replace: true });
    } else {
      navigate(-1);
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      setShowCompanyInfo(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div
      style={{ zIndex: isMenuOpen ? CHROME_Z_INDEX.menuDrawer : CHROME_Z_INDEX.bar }}
      className={cn("fixed top-0 left-0 right-0 w-full max-w-shell m-auto", className)}
    >
      <ChromeBar>
        <div className="flex w-full items-center justify-between gap-3 px-4">
          <div className="flex min-w-0 items-center gap-3">
            {showBackButton ? (
              <IconButton icon={Back} label="Voltar" tone="primary" size="icon-bar" onClick={handleBackButton} />
            ) : (
              <IconButton icon={List} label="Abrir menu" tone="primary" size="icon-bar" onClick={toggleMenu} />
            )}

            {!title && showCompanyInfo ? (
              <div
                className={cn(
                  "flex min-w-0 flex-col gap-1 transition-[opacity,transform] duration-slow ease-standard",
                  showCompanyInfo ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Surface surface="page" className="w-8 h-8 shrink-0 rounded-full overflow-hidden">
                    <ImageWithFallback
                      width={32}
                      height={32}
                      alt="logo"
                      className="w-full h-full object-cover"
                      src={basicInfo?.logoImage?.URL}
                      fallbackClassName="w-full h-full"
                    />
                  </Surface>
                  <Text as="span" size="xl" weight="bold" truncate className="min-w-0 whitespace-nowrap">
                    {basicInfo.name}
                  </Text>
                </div>
              </div>
            ) : (
              <Text as="span" size="lg" weight="semibold" className="mt-1">
                {title || ""}
              </Text>
            )}
          </div>

          {!title && (
            <ExpandingSearch
              open={searchOpen}
              onOpenChange={setSearchOpen}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
          )}
        </div>
      </ChromeBar>
      {isMenuOpen && !showBackButton && <Scrim className="z-raised" onClick={closeMenu} />}

      {!showBackButton && (
        <Surface
          surface="card"
          className={cn(
            "fixed top-0 left-0 h-full w-80 flex flex-col border-r border-card-text/10 transform transition-transform duration-slow ease-standard z-raised-2",
            isMenuOpen ? "translate-x-0 shadow-overlay" : "-translate-x-full"
          )}
        >
          <Surface surface="card" className="p-6 border-b border-card-text/10">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <Surface surface="page" className="w-12 h-12 shrink-0 rounded-full overflow-hidden">
                  <ImageWithFallback
                    width={48}
                    height={48}
                    alt="logo"
                    className="w-full h-full object-cover"
                    src={basicInfo?.logoImage?.URL}
                    fallbackClassName="w-full h-full"
                  />
                </Surface>
                <Text
                  as="span"
                  size="lg"
                  weight="bold"
                  clamp={2}
                  className="min-w-0 break-words"
                  title={basicInfo.name}
                >
                  {basicInfo.name}
                </Text>
              </div>
              <IconButton icon={Close} label="Fechar menu" size="icon-sm" iconSize="md" onClick={closeMenu} className="self-start" />
            </div>
          </Surface>

          <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
            <nav className="flex-1 min-h-0 flex flex-col justify-between">
              <div className="space-y-2">
                {NAV_ITEMS.map(({ to, icon, label, className: itemClassName }) => (
                  <ListRow
                    key={to}
                    as="link"
                    to={to}
                    variant="menu"
                    onClick={closeMenu}
                    className={itemClassName}
                    leading={
                      <span className={MENU_ICON_BOX_CLASS}>
                        <Icon as={icon} size="2xl" weight="bold" tone="primary" />
                      </span>
                    }
                    title={label}
                    tone="default"
                  />
                ))}
              </div>

              {isSupported && (
                <ListRow
                  as="button"
                  variant="menu"
                  onClick={() => {
                    toggleFullscreen();
                    closeMenu();
                  }}
                  leading={
                    <span className={MENU_ICON_BOX_CLASS}>
                      <Icon as={isFullscreen ? ArrowsIn : ArrowsOut} size="2xl" weight="bold" tone="primary" />
                    </span>
                  }
                  title={isFullscreen ? "Sair tela cheia" : "Tela cheia"}
                  tone="default"
                />
              )}
            </nav>
          </div>
        </Surface>
      )}
    </div>
  );
}
